// THE GENESIS ERROR CODES SPARK RECOGNISES, IN ONE PLACE.
//
// genesis catalogues its deliberate refusals: a `ServiceException` subclass carries an
// `ErrorMessage` with a code, and genesis's exception resolver copies that code into
// `extensions.code` on the wire. spark matches on those codes to tell a refusal apart from a
// failure, and to answer each one with the repair it actually calls for.
//
// WHY THIS FILE EXISTS. These strings are a contract between two repositories that nothing enforced:
// they were spelled out as literals at each call site, so a code that genesis renumbered or reused
// would go on matching nothing — or worse, matching the wrong refusal — with no test failing and no
// type breaking, because to TypeScript they are just strings (jfalkenstein, !238 note_25773).
// Collecting them here means a reader can see the whole cross-repo surface at once, and gives
// genesis's contract test a definite list to be checked against.
//
// HOW IT IS GUARDED. The real guard is in genesis, not here:
// `GraphQlMessagesPublishedContractTest` pins the id of every message on this list, so renumbering
// one fails in the repository doing the renumbering — before it can reach spark at all. A guard on
// this side could only notice after somebody re-synced the schema, which is too late to be useful.
//
// What it cannot do: catch genesis giving the same id a different MEANING. Nothing mechanical can.
// That is why each entry below states what the refusal means rather than only naming it — a reviewer
// comparing the two sides has something to compare.
//
// ADDING ONE: add it here with what it means and which operation raises it, add the same id to
// genesis's GraphQlMessagesPublishedContractTest, and import the constant. Never inline the string.

/**
 * "This document is still used by a shared item, so it cannot be deleted."
 *
 * Raised by `deleteDocument`. Unusually, its extensions carry data spark reads — `sharedItemIds` —
 * so the page can name what is in the way rather than only that something is.
 */
export const DOCUMENT_USED_IN_SHARED_ITEMS = "GENESIS001218";

/**
 * "This would leave the company with no usable administrator."
 *
 * Raised by `removeCompanyMember` and `setCompanyMemberRole`. Matched by code rather than by
 * classification: it used to be the only VALIDATION those mutations could reach, which stopped
 * being true the moment {@link PERSON_CANNOT_ADMINISTER_COMPANY} appeared on the same two.
 */
export const LAST_COMPANY_ADMIN = "GENESIS001240";

/**
 * "That would be one contact person too many."
 *
 * Raised by `setCompanyContactPerson`. The cap is CompanyMembershipController.MAX_CONTACT_PERSONS,
 * ten since genesis#268, and the company page was amended to ten in concept v13 — code and
 * concept agree, so the concept's number IS the enforced one.
 *
 * Nothing here spells the number out because nothing here needs it: this module matches on the code
 * alone. The sentence a reader sees is built in use-company-admin.ts from spark's own
 * MAX_CONTACT_PERSONS, which is the SECOND pin of the same number — genesis parameterises its
 * message, but spark never parses the number back out of it. Both pins move together or spark
 * refuses at the old cap and quotes it.
 */
export const TOO_MANY_CONTACT_PERSONS = "GENESIS001241";

/**
 * "That person's account is not active, so they cannot administer a company."
 *
 * MUST stay distinguishable from {@link LAST_COMPANY_ADMIN}: the two ask for opposite repairs. One
 * says promote somebody else before you leave; this one says the person you just picked cannot be
 * promoted at all. Answering either with the other's wording sends the admin round in a circle.
 */
export const PERSON_CANNOT_ADMINISTER_COMPANY = "GENESIS001242";

// GENESIS001244, GENESIS001245 and GENESIS001246 USED TO BE HERE AND ARE BURNT. They belonged to
// the shared competency-field catalogue, which genesis deleted along with the vocabulary itself
// (@external-adr GENESIS-0031-company-expertise-is-per-company-free-text): expertise is now per-company free
// text, so there is no catalogue to clash with, to find in use, or to be refused write access to.
// genesis's GraphQlMessagesPublishedContractTest#retiredIdsAreNotRecycled fails its build if the
// three numbers are ever reissued, so they are not coming back with a different meaning — which is
// why this note is a note and not a constant. In particular 001247 below is NOT 001244 renumbered:
// 001244 said "the platform already has this field, use that one", which is a sentence nothing on
// the platform can say any more.

/**
 * "This company already lists that expertise."
 *
 * Raised by `setCompanyCompetency`, which ADDS rather than upserts. Compared case-insensitively and
 * ignoring surrounding whitespace, and scoped to ONE company: the same label at another company is
 * a different row and no clash at all. The repair is to remove the existing entry first — changing
 * a degree is a remove followed by an add, because that is the only editing model the screen has.
 *
 * Matched by CODE, not by classification: this mutation reaches several VALIDATION refusals
 * (001227 degree, 001228 blank, 001229 over-length), and a loose match would put the duplicate's
 * wording on any of them.
 */
export const COMPANY_EXPERTISE_ALREADY_LISTED = "GENESIS001247";

/**
 * "This company already lists as many expertise entries as genesis allows."
 *
 * Raised by `setCompanyCompetency` when the company already holds
 * `CompanyCompetencyController.MAX_COMPETENCIES` rows. The editor holds its own copy of that number
 * and disables Add before the click, so this is the backstop for the two copies drifting apart or for
 * another admin filling the last slot in a second session — the same arrangement as
 * {@link TOO_MANY_CONTACT_PERSONS}.
 *
 * MUST stay distinguishable from {@link COMPANY_EXPERTISE_LABEL_TOO_LONG}: both are raised by this one
 * mutation and both are about a limit, but one is repaired by shortening the label and the other by
 * removing an entry. Answering either with the other's wording sends the admin to edit a box that is
 * not the problem.
 *
 * It also outranks {@link COMPANY_EXPERTISE_ALREADY_LISTED} on genesis's side: at the cap, re-adding a
 * label the company already lists is refused as full rather than as a duplicate, because "the list is
 * full" is true whatever was sent.
 */
export const COMPANY_EXPERTISE_LIST_FULL = "GENESIS001249";

/**
 * "This company is inactive — nothing can be published in its name, and its content is frozen."
 *
 * Raised by `createSharedItem` when the named company is INACTIVE, and by `updateSharedItem`,
 * `deleteSharedItem`, `signUpSharedProject`, `rateSharedItem` and `viewSharedItem` for an item that
 * company owns. Concept v9: a dormant company's content can be read, and nothing else — see
 * the company account model.
 *
 * <b>And since concept v16 by the three COMMENT mutations too</b> —
 * `createSharedItemComment` for a comment written in an inactive company's name, and
 * `updateSharedItemComment` / `deleteSharedItemComment` for one that already carries its
 * `company_id`. v16 closed the open point v8/v9 had left ("Nicht mitgesperrt sind Kommentare"),
 * which stood only because there was no company comment yet to freeze. A PERSONAL comment on a
 * dormant company's content is still allowed and still raises nothing: "Gesperrt ist die
 * Firmenstimme, nicht die Diskussion."
 *
 * <b>It must not be collapsed into the generic authorisation refusal, and that is the whole reason
 * it has its own code.</b> The two are repaired in OPPOSITE directions: this one by reactivating the
 * company, the other by being made an admin — and the caller who sees this one already IS an admin.
 * Telling them to ask for access would send them to the one place that cannot help.
 */
export const COMPANY_IS_INACTIVE = "GENESIS001250";

/**
 * "This company's content can only be written while acting as that company."
 *
 * Raised by `updateSharedItem`, `deleteSharedItem`, `updateSharedItemComment` and
 * `deleteSharedItemComment` when the write carries no `actingAsCompanyId`, or one naming a company
 * other than the `company_id` stored on the row — and by `createSharedItemComment` when the
 * declaration contradicts the `data.company` the comment is written under. Concept v23
 * (the company account model, Kevin 2026-09-11) narrows editing and deleting a
 * company's post, project or comment to two conditions where there was one: a current `admin`
 * membership AND acting as that company at the time of the write.
 *
 * <b>It must stay distinguishable from {@link COMPANY_IS_INACTIVE} and from the uncatalogued "does
 * not administer company" refusal, and the three are repaired in three different places.</b> 001250
 * is repaired by reactivating the company, which nobody can do to a company that is perfectly
 * active; the permission refusal is repaired by being made an admin; this one is repaired inside
 * spark, by switching into the company and repeating the write. A surface that cannot tell them
 * apart gives two callers out of three an instruction they cannot act on.
 *
 * <b>Reachable in ordinary use, which is why it is mapped at all.</b> spark hides Edit and Delete
 * while the viewer is not acting as the owning company (`mayManageItem`, `mayManageComment`), so this
 * is not the button being pressed anyway — it is the page that was rendered before the context
 * changed. The acting context lives in memory and dies with the tab, so an open page can go stale
 * between render and submit, and concept v23 asks in as many words for a usable answer there rather
 * than "no permission".
 *
 * <b>Not a permission, and the wording must not imply one.</b> v23 is explicitly not an
 * authorisation boundary (genesis#300): the value is client-declared, every principal who could
 * already write can send it, and the caller who meets this refusal holds the admin membership that
 * authorises the write.
 */
export const NOT_ACTING_AS_COMPANY = "GENESIS001259";

/**
 * "Your own content can only be created, edited or deleted while not acting as a company."
 *
 * The MIRROR of {@link NOT_ACTING_AS_COMPANY}, and the second direction of ONE rule rather than a
 * second rule: the declared acting context and the ownership of the content must MATCH. Raised by
 * `updateSharedItem`, `deleteSharedItem`, `updateSharedItemComment` and `deleteSharedItemComment`
 * when a write on the caller's OWN content carries an `actingAsCompanyId` at all — and, since the
 * same extension removed creation's exemption, by `createSharedItem` and `createSharedItemComment`
 * when the declaration is made over content with no `data.company`. Concept v23
 * (the company account model), extended by Kevin on 2026-09-12: "if someone
 * acts as a company, he cannot edit/delete/create comments as private person."
 *
 * <b>Its own code because the repair is the OPPOSITE one.</b> 001259 is repaired by switching INTO a
 * company and this one by LEAVING it, so a surface that could not tell the pair apart would answer
 * half its callers with the instruction that takes them further from the write they are attempting.
 * The two are exhaustive by construction — a declared context and a content owner either agree or
 * they do not, and each code covers one of the two ways they can disagree.
 *
 * <b>Reachable in ordinary use, which is why it is mapped at all.</b> spark withholds Edit and Delete
 * from personal content while a company context is active (`mayManageItem`, `mayManageComment`), so
 * this is not the button being pressed anyway — it is the page that was rendered before the context
 * changed. The acting context lives in memory and dies with the tab, and concept v23 asks for a usable
 * answer in that gap rather than "no permission".
 *
 * <b>Not a permission, and the wording must not imply one.</b> The caller OWNS this content and may
 * write it; they are standing in the wrong mode, not short of a right. v23 is expressly not an
 * authorisation boundary (genesis#300).
 */
export const ACTING_AS_COMPANY_ON_OWN_CONTENT = "GENESIS001260";

/**
 * "That expertise label is longer than genesis stores."
 *
 * Raised by `setCompanyCompetency` when the trimmed label exceeds 255 characters. The editor holds
 * its own copy of that cap and disables Add before the click, so this is the backstop for the two
 * copies drifting apart — the same arrangement as MAX_CONTACT_PERSONS, and for the same reason:
 * genesis is the authority and a client-side cap that is merely out of date must still produce an
 * actionable sentence rather than "something went wrong".
 *
 * Its siblings are deliberately NOT listed. GENESIS001228 ("label must not be empty") and
 * GENESIS001227 ("degree must be between 0 and 100") are unreachable by construction rather than by
 * a copied constant: a blank Add is disabled on the trimmed value the click would send, and every
 * degree comes from the shared seven-level scale, whose steps are percentages. Neither depends on a
 * number that can drift, so neither needs a backstop.
 */
export const COMPANY_EXPERTISE_LABEL_TOO_LONG = "GENESIS001229";

/**
 * "A match holds at most N companies."
 *
 * Raised by `createMatchCompany` and `createMatchCompanies` when the write would push the match's
 * company pool past `MatchCompanyController.MAX_MATCH_COMPANIES` (2000). Counted inside the insert's
 * transaction against the Match row lock, so it is the real bound and not an estimate: two
 * concurrent batches cannot both take the last slot.
 *
 * A VALIDATION refusal, not a permission one — the caller was allowed to write, there is simply no
 * room. It MUST stay distinguishable from genesis's `cantModifyOtherMatch`, which is the refusal on
 * the other case and is uncoded: answering "no room" with "you may not edit this match" would send
 * the user to ask for rights they already have, and answering it the other way round would tell
 * someone without rights to delete rows they cannot delete.
 *
 * spark holds its own copy of the number (MATCH_COMPANY_LIMIT in components/match/match-constants)
 * and says "full" before the click, so reaching this means the two copies drifted or another editor
 * filled the last slot in a second session — the same arrangement as {@link TOO_MANY_CONTACT_PERSONS}.
 *
 * <b>There is no person twin of this code, and that is not an omission.</b> The person pool carries
 * no server-side cap at all (genesis#279), so a shared "too many pool entries" code would imply a
 * rule on a path that does not enforce one.
 */
export const TOO_MANY_MATCH_COMPANIES = "GENESIS001258";

/**
 * The company ranking asked the model for more judgements than one run may make
 * (`model-gateway.company-match-decision-ceiling`, default 10 000).
 *
 * <b>Named rather than left generic, because the repair is the user's.</b> genesis refuses the run
 * instead of truncating it — a truncated ranking is indistinguishable from an honest one, since an
 * unjudged company legitimately reads "nicht bewertbar". So the reader sees a failure, and the only
 * two things that fix it are theirs or their operator's: fewer expertise criteria in the design, or a
 * higher configured ceiling. A generic message would offer a retry that cannot succeed.
 */
export const COMPANY_AI_CEILING_EXCEEDED = "GENESIS001256";

/**
 * A company expertise criterion whose requirement text is blank.
 *
 * <b>Refused by genesis and not only by the editor.</b> spark blocks the run while such a row exists,
 * but that gate is presentation: a caller posting straight at genesis, or a design that stored a blank
 * row earlier, reaches the run anyway. Asking the model whether a company has the expertise "" is a
 * question with no answer, so genesis refuses rather than judging — and this code lets spark say which
 * row to fill instead of showing a server sentence.
 */
export const BLANK_COMPANY_EXPERTISE_REQUIREMENT = "GENESIS001257";

/**
 * "This listing image is larger than genesis takes."
 *
 * Raised by `createListing` and `updateListing` when the DECODED bytes of the Titelbild or of a
 * weiteres Bild exceed `ImageContentValidation.MAX_IMAGE_BYTES` (5 MiB). spark pins the same number
 * as MAX_LISTING_IMAGE_BYTES and refuses the file at the picker, so this is the backstop for the two
 * copies drifting apart. Repaired by choosing a smaller file, never by retrying.
 */
export const LISTING_IMAGE_TOO_LARGE = "GENESIS001282";

/**
 * "This listing image is not a PNG, JPEG, GIF, BMP, TIFF or WebP."
 *
 * Raised by `createListing` and `updateListing`, decided on the BYTES. spark's picker can only judge
 * the type the browser reports, so a renamed file (an HEIC called .jpg, an SVG) passes it and lands
 * here. MUST stay distinguishable from {@link LISTING_IMAGE_TOO_LARGE}: one asks for a smaller file,
 * the other for a different format.
 */
export const LISTING_IMAGE_UNSUPPORTED = "GENESIS001283";


/**
 * Every code above. Kept beside the constants deliberately: this list is what a reviewer diffs
 * against genesis's published-contract test, and a code added without an entry here is a code that
 * quietly leaves the contract.
 */
export const ALL_GENESIS_ERROR_CODES = [
  DOCUMENT_USED_IN_SHARED_ITEMS,
  LAST_COMPANY_ADMIN,
  TOO_MANY_CONTACT_PERSONS,
  PERSON_CANNOT_ADMINISTER_COMPANY,
  COMPANY_EXPERTISE_ALREADY_LISTED,
  COMPANY_EXPERTISE_LIST_FULL,
  COMPANY_EXPERTISE_LABEL_TOO_LONG,
  COMPANY_IS_INACTIVE,
  NOT_ACTING_AS_COMPANY,
  ACTING_AS_COMPANY_ON_OWN_CONTENT,
  TOO_MANY_MATCH_COMPANIES,
  COMPANY_AI_CEILING_EXCEEDED,
  BLANK_COMPANY_EXPERTISE_REQUIREMENT,
  LISTING_IMAGE_TOO_LARGE,
  LISTING_IMAGE_UNSUPPORTED,
] as const;

/**
 * "The platform logo (or background image) is over the inline ceiling."
 *
 * Raised by `updateSystemSettings` (genesis#372). The ceiling is
 * `ImageContentValidation.MAX_INLINE_IMAGE_BYTES`, pinned a second time as spark's
 * MAX_BRAND_LOGO_BYTES, which refuses the same file earlier and by name; genesis parameterises its
 * message with the byte counts and spark never parses them back out. Deliberately NOT the company
 * logo's ceiling: that one lives in backdrop and is ten times the number.
 */
export const BRAND_IMAGE_TOO_LARGE = "GENESIS001269";

/**
 * "The platform logo's bytes are not a PNG, JPEG, GIF, BMP, TIFF or WebP, whatever the declared
 * type says."
 *
 * Raised by `updateSystemSettings` (genesis#372), the Branding twin of the company logo's format
 * refusal, under its own code so the message lands beside the Branding picker.
 */
export const BRAND_IMAGE_UNSUPPORTED = "GENESIS001270";

/**
 * "The member list of company %s is only readable inside the organisation."
 *
 * Raised by `companyMembers` for a caller who does not read INTERNAL — concept v25 (2026-09-28,
 * the company page). The company page's two lists now answer two
 * audiences: the member roster is internal, the contact strip stays open to everyone signed in (§4).
 *
 * <b>It must not be treated as a failure, and that is the whole reason spark matches on it.</b>
 * Without the code the refusal folds into `useCompany`'s page-level error and `CompanyPage` renders
 * "Company not found" — telling an external reader that an ordinary company does not exist, and
 * taking the contact strip down with it. Recognised, the page simply draws without the roster.
 *
 * NOT `GENESIS001269`, where genesis first put it: 269 and 270 were already published for the
 * SystemSettings image pair above, and the genesis branch had been sized against a stale main.
 */
export const COMPANY_MEMBERS_ARE_INTERNAL = "GENESIS001271";

/**
 * "You neither created this listing nor manage its access."
 *
 * Raised by `setListingAccess` and by `updateListing` with `access`
 * (@external-adr GENESIS-0095-managing-listing-access-is-separate-from-editing-it). WRITE on the listing text does
 * not count. Matched so the members dialog can say the capability is missing or was withdrawn while it was open,
 * instead of the generic permission sentence.
 */
export const LISTING_ACCESS_NOT_MANAGED = "GENESIS001291";

/**
 * "Only the creator appoints access managers or changes a manager's entry."
 *
 * Raised by the same two mutations for a delegated access manager who reached beyond their scope. MUST stay
 * distinct from {@link LISTING_ACCESS_NOT_MANAGED}: this caller does manage the access, the repair is to ask the
 * creator.
 */
export const LISTING_ACCESS_DELEGATION_REFUSED = "GENESIS001292";

/**
 * "The access list changed since it was read."
 *
 * Raised by `setListingAccess` when its `expectedRevision` is stale. Nothing was written; the repair is to reopen
 * the dialog and decide again on the current list. A VALIDATION refusal, not a permission one.
 */
export const LISTING_ACCESS_CHANGED = "GENESIS001293";

/**
 * "You may grant at most your own level on applications."
 *
 * Raised by `setListingAccess` and by `updateListing` with `access` for a delegated access manager who gives or raises
 * a level on applications above the one they hold
 * (@external-adr GENESIS-0096-listing-applications-are-read-only-by-their-explicit-readers). An unchanged higher entry
 * is not refused. MUST stay distinct from {@link LISTING_ACCESS_DELEGATION_REFUSED}: the entry is theirs to change,
 * only not to that level.
 */
export const LISTING_APPLICATIONS_GRANT_REFUSED = "GENESIS001290";

/**
 * "The mail preview is no longer current."
 *
 * Raised by `sendListingApplicationMail` when recipient, language, template revision or application version differ from
 * the stored preview (@external-adr GENESIS-0080-an-application-mail-sends-exactly-the-confirmed-preview-to-a-proven-address).
 * Nothing was queued; the dialog keeps the edited text and asks for a new preview.
 */
export const LISTING_APPLICATION_MAIL_PREVIEW_STALE = "GENESIS001294";

/**
 * "The mail preview has expired, was sent already or does not exist."
 *
 * Raised by `sendListingApplicationMail`; the four cases answer alike. The dialog asks for a new preview.
 */
export const LISTING_APPLICATION_MAIL_PREVIEW_GONE = "GENESIS001295";

/**
 * "The request was already used for another mail."
 *
 * Raised by `sendListingApplicationMail` for a request id that stands for another mail. Carries no data of that mail;
 * the dialog starts a new confirmation with a fresh id.
 */
export const LISTING_APPLICATION_MAIL_REQUEST_USED = "GENESIS001296";

/**
 * "Sending mail is not configured on this server."
 *
 * Raised by `previewListingApplicationMail` and `sendListingApplicationMail`. An operator's repair, not the sender's.
 */
export const MAIL_NOT_CONFIGURED = "GENESIS001297";

/**
 * "This mail cannot be sent to an application in this state."
 *
 * Raised by both mail mutations when the state changed meanwhile: an interview to a decided application, any mail to a
 * withdrawn one.
 */
export const LISTING_APPLICATION_MAIL_NOT_ALLOWED = "GENESIS001298";

// THE ROLEPLAY GAME'S REFUSALS (genesis#396, genesis!481; spark#1075). Raised by the calls spark makes for the plane
// game itself (play contract 3, §2.3): the game session head's `startGameSession` and `restartGameSession`, which take
// the game key (`roleplay.window-seat`), and the session's own `myRoleplaySession`, `setRoleplaySessionPowers` and
// `lockRoleplaySessionPowers`. genesis's GraphQlMessagesPublishedContractTest pins them. 1287 was 1284 until
// genesis#396 merged main: main's GENESIS001284 is "The design version '%s' is not supported", and the listing
// branches hold 1282 and 1283. The not-found one moved from 1285 to 1343 when genesis#396 merged main again
// (genesis!481): main's GENESIS001285 is genesis#415's "Match %s has too little project text to derive reliable
// requirements from". 1287 and 1288 stayed; 1286 was retired with the clusters (spark#1075, Entscheidung 127.9).

/**
 * "Roleplay session %s not found."
 *
 * Raised for a session id the player cannot reach: an unknown one and, on purpose, another person's session alike, so
 * the refusal tells nobody that a session exists. spark sends the player back to Play.
 */
export const ROLEPLAY_GAME_NOT_FOUND = "GENESIS001343";

/** The selected powers can no longer change once the first move, or agent-service's first readings, locked them. */
export const ROLEPLAY_POWERS_LOCKED = "GENESIS001344";

/** A selected list must contain one to five distinct Powers. */
export const ROLEPLAY_POWERS_INVALID = "GENESIS001345";

/**
 * "Roleplay session %s is no longer open."
 *
 * Raised by a change or lock of the chosen Powers of a session that is completed, abandoned by a restart or expired
 * in the meantime, from another tab for instance. spark reads the session again and shows the game closed.
 */
export const ROLEPLAY_GAME_NOT_OPEN = "GENESIS001287";

/**
 * "The game %s is paused: no new session can start."
 *
 * Raised by `startGameSession` when it would start a new session of a paused game, and by `restartGameSession` of a
 * paused game before anything changes (decisions 75 and 76 of smycles&40). A running session is still returned by
 * `startGameSession` and can be finished. spark says the game is temporarily unavailable and reads the catalogue again.
 */
export const GAME_PAUSED = "GENESIS001288";
